import {
    AlertCircle,
    Building2,
    CheckCircle,
    Clock,
    Edit,
    Eye,
    FileText,
    Filter,
    Plus,
    Search,
    Trash2,
    UserPlus,
    X
} from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';
import SiteContentManagement from './SiteContentManagement';
import SubscriptionBillingManagement from './SubscriptionBillingManagement';

const SuperAdminDashboard = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRMC, setSelectedRMC] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('companies'); // 'companies', 'applications', 'promotions', 'subscription-billing', or 'site-contents'
  const [showApplicationDetailModal, setShowApplicationDetailModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showPromotionDetailModal, setShowPromotionDetailModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [adminStatusMap, setAdminStatusMap] = useState({});
  const queryClient = useQueryClient();

  // 임대관리사업자 목록 조회
  const { data: rmcList, isLoading } = useQuery(
    'rental-management-companies',
    () => api.get('/super-admin/rental-management-companies'),
    {
      onError: (error) => {
        toast.error('임대관리사업자 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  // 임대관리사업자 목록이 로드되면 관리자 상태 확인
  React.useEffect(() => {
    if (rmcList && rmcList.length > 0) {
      console.log('RMC 목록 로드됨, 관리자 상태 확인 시작:', rmcList.length);
      rmcList.forEach(rmc => {
        if (rmc.id) {
          checkAdminStatus(rmc.id);
        }
      });
    }
  }, [rmcList]);

  // 가입 신청 목록 조회
  const { data: applicationsResponse, isLoading: applicationsLoading } = useQuery(
    'applications',
    () => api.get('/super-admin/applications'),
    {
      onError: (error) => {
        toast.error('가입 신청 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  const applicationsList = applicationsResponse?.data?.applications || [];

  // 프로모션 목록 조회
  const { data: promotionsResponse, isLoading: promotionsLoading } = useQuery(
    'promotions',
    () => api.get('/super-admin/promotions'),
    {
      onError: (error) => {
        toast.error('프로모션 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  const promotionsList = promotionsResponse?.promotions || [];

  // 디버깅 로그
  console.log('🔍 promotionsResponse:', promotionsResponse);
  console.log('🔍 promotionsResponse.promotions:', promotionsResponse?.promotions);
  console.log('🔍 promotionsList:', promotionsList);
  console.log('🔍 promotionsList length:', promotionsList.length);

  // 사이트 콘텐츠 목록 조회
  const { data: siteContentsResponse, isLoading: siteContentsLoading } = useQuery(
    'site-contents',
    () => api.get('/super-admin/site-contents'),
    {
      onError: (error) => {
        toast.error('사이트 콘텐츠 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  const siteContentsList = siteContentsResponse?.data || [];
  const [showSiteContentModal, setShowSiteContentModal] = useState(false);
  const [selectedSiteContent, setSelectedSiteContent] = useState(null);
  const [siteContentType, setSiteContentType] = useState('footer');

  // 임대관리사업자 등록
  const addRMCMutation = useMutation(
    (rmcData) => api.post('/super-admin/rental-management-companies', rmcData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('rental-management-companies');
        setShowAddModal(false);
        toast.success('임대관리사업자가 등록되었습니다.');

        // 로그인 정보가 있으면 알림
        if (data.login_info) {
          alert(`✅ 임대관리사업자 등록 완료!\n\n📧 로그인 이메일: ${data.login_info.email}\n🔑 로그인 비밀번호: ${data.login_info.password}\n\n💡 이 정보를 안전한 곳에 보관하고, 생성된 계정으로 바로 로그인할 수 있습니다.`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '등록에 실패했습니다.');
      }
    }
  );

  // 임대관리사업자 수정
  const updateRMCMutation = useMutation(
    ({ rmcId, rmcData }) => api.put(`/super-admin/rental-management-companies/${rmcId}`, rmcData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rental-management-companies');
        setShowEditModal(false);
        setSelectedRMC(null);
        toast.success('임대관리사업자 정보가 수정되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '수정에 실패했습니다.');
      }
    }
  );

  // 계정 생성
  const createAccountMutation = useMutation(
    ({ rmcId, accountData }) => api.post(`/super-admin/rental-management-companies/${rmcId}/create-account`, accountData),
    {
      onSuccess: (data, variables) => {
        // 강제로 캐시 무효화 및 새로고침
        queryClient.removeQueries('rental-management-companies');
        queryClient.invalidateQueries('rental-management-companies');
        setShowCreateAccountModal(false);
        toast.success('계정이 생성되었습니다!');
        
        // 관리자 상태 업데이트
        checkAdminStatus(variables.rmcId);

        // 사용자 친화적인 성공 메시지
        const loginInfo = data.login_info;
        alert(`✅ 계정 생성 완료!\n\n📧 로그인 이메일: ${loginInfo.email}\n🔑 로그인 비밀번호: ${loginInfo.password}\n\n💡 이 정보를 안전한 곳에 보관하고, 생성된 계정으로 바로 로그인할 수 있습니다.`);

        // 페이지 새로고침으로 상태 강제 업데이트
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '계정 생성에 실패했습니다.');
      }
    }
  );

  // 가입 신청 승인
  const approveApplicationMutation = useMutation(
    ({ applicationId, initialPassword }) => api.post(`/super-admin/applications/${applicationId}/approve`, { initial_password: initialPassword }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('applications');
        queryClient.invalidateQueries('rental-management-companies');
        setShowApplicationDetailModal(false);
        setSelectedApplication(null);
        toast.success('가입 신청이 승인되었습니다.');

        // 로그인 정보 알림
        if (data.login_info) {
          alert(`✅ 가입 승인 완료!\n\n📧 로그인 이메일: ${data.login_info.email}\n🔑 로그인 비밀번호: ${data.login_info.password}\n\n💡 이 정보를 신청자에게 전달해주세요.`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '승인 처리에 실패했습니다.');
      }
    }
  );

  // 가입 신청 거부
  const rejectApplicationMutation = useMutation(
    ({ applicationId, rejectionReason }) => api.post(`/super-admin/applications/${applicationId}/reject`, { rejection_reason: rejectionReason }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('applications');
        setShowApplicationDetailModal(false);
        setSelectedApplication(null);
        toast.success('가입 신청이 거부되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '거부 처리에 실패했습니다.');
      }
    }
  );

  // 프로모션 생성
  const createPromotionMutation = useMutation(
    (promotionData) => api.post('/super-admin/promotions', promotionData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('promotions');
        setShowPromotionModal(false);
        toast.success('프로모션이 생성되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '프로모션 생성에 실패했습니다.');
      }
    }
  );

  // 프로모션 수정
  const updatePromotionMutation = useMutation(
    ({ promotionId, promotionData }) => api.put(`/super-admin/promotions/${promotionId}`, promotionData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('promotions');
        setShowPromotionDetailModal(false);
        setSelectedPromotion(null);
        toast.success('프로모션이 수정되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '프로모션 수정에 실패했습니다.');
      }
    }
  );

  // 프로모션 삭제
  const deletePromotionMutation = useMutation(
    (promotionId) => api.delete(`/super-admin/promotions/${promotionId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('promotions');
        toast.success('프로모션이 삭제되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '프로모션 삭제에 실패했습니다.');
      }
    }
  );

  // 프로모션 활성화/비활성화
  const togglePromotionMutation = useMutation(
    (promotionId) => api.post(`/super-admin/promotions/${promotionId}/toggle`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('promotions');
        toast.success('프로모션 상태가 변경되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '프로모션 상태 변경에 실패했습니다.');
      }
    }
  );

  // 임대관리사업자 삭제
  const deleteRMCMutation = useMutation(
    (rmcId) => api.delete(`/super-admin/rental-management-companies/${rmcId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rental-management-companies');
        toast.success('임대관리사업자가 삭제되었습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '삭제에 실패했습니다.');
      }
    }
  );

  const handleAddRMC = (formData) => {
    addRMCMutation.mutate(formData);
  };

  const handleUpdateRMC = (formData) => {
    updateRMCMutation.mutate({
      rmcId: selectedRMC.id,
      rmcData: formData
    });
  };

  const handleCreateAccount = (formData) => {
    createAccountMutation.mutate({
      rmcId: selectedRMC.id,
      accountData: formData
    });
  };

  const handleDeleteRMC = (rmcId) => {
    if (window.confirm('정말로 이 임대관리사업자를 삭제하시겠습니까?')) {
      deleteRMCMutation.mutate(rmcId);
    }
  };

  const handleViewDetail = (rmc) => {
    setSelectedRMC(rmc);
    setShowEditModal(false);
    setShowCreateAccountModal(false);
  };

  // 관리자 계정 상태 확인
  const checkAdminStatus = async (rmcId) => {
    try {
      const response = await api.get(`/super-admin/rental-management-companies/${rmcId}/admin-status`);
      console.log(`관리자 상태 확인 (RMC ${rmcId}) - 응답 전체:`, response);
      console.log(`관리자 상태 확인 (RMC ${rmcId}) - 응답 타입:`, typeof response);
      console.log(`관리자 상태 확인 (RMC ${rmcId}) - 응답 키들:`, response ? Object.keys(response) : 'response is null/undefined');
      
      // api.get()은 response.data를 반환하므로 response 자체가 데이터
      const data = response;
      
      if (data && typeof data === 'object') {
        console.log(`관리자 상태 확인 (RMC ${rmcId}) - 데이터 설정:`, data);
        setAdminStatusMap(prev => ({
          ...prev,
          [rmcId]: data
        }));
      } else {
        console.log(`관리자 상태 확인 (RMC ${rmcId}) - 데이터가 없음, 기본값 설정`);
        setAdminStatusMap(prev => ({
          ...prev,
          [rmcId]: { has_admin: false, admin_user: null }
        }));
      }
    } catch (error) {
      console.error(`관리자 상태 확인 실패 (RMC ${rmcId}):`, error);
      console.error('에러 상세:', error.response?.data || error.message);
      // 에러가 발생해도 상태를 업데이트 (has_admin: false)
      setAdminStatusMap(prev => ({
        ...prev,
        [rmcId]: { has_admin: false, admin_user: null }
      }));
    }
  };

  const handleEdit = (rmc) => {
    setSelectedRMC(rmc);
    setShowEditModal(true);
    setShowCreateAccountModal(false);
  };

  const getActivationStatusBadge = (status) => {
    switch (status) {
      case 'pending_activation':
      case 'account_created':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="mr-1 h-3 w-3" />
          계정 생성 필요
        </span>;
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="mr-1 h-3 w-3" />
          사용 가능
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {status}
        </span>;
    }
  };

  const filteredRMCList = rmcList?.filter(rmc =>
    rmc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rmc.email.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div className="space-y-6">
      {/* 페이지 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">총괄관리자 대시보드</h1>
          <p className="mt-1 text-sm text-gray-500">
            임대관리사업자들을 관리하세요
          </p>
        </div>
        {activeTab === 'companies' && (
          <button
            onClick={() => { window.location.href = '/super-admin/softflo-rmc-applications'; }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="mr-2 h-4 w-4" />
            SoftFlo 승인대기
          </button>
        )}
        {activeTab === 'promotions' && (
          <button
            onClick={() => setShowPromotionModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="mr-2 h-4 w-4" />
            프로모션 생성
          </button>
        )}
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('companies')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'companies'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <Building2 className="inline-block mr-2 h-4 w-4" />
            임대관리사업자 관리
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'applications'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <UserPlus className="inline-block mr-2 h-4 w-4" />
            가입 신청 관리
            {applicationsList?.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {applicationsList.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('promotions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'promotions'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <Building2 className="inline-block mr-2 h-4 w-4" />
            프로모션 관리
            {promotionsList?.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {promotionsList.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('subscription-billing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'subscription-billing'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <AlertCircle className="inline-block mr-2 h-4 w-4" />
            구독료 관리
          </button>
          <button
            onClick={() => setActiveTab('site-contents')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'site-contents'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <FileText className="inline-block mr-2 h-4 w-4" />
            사이트 콘텐츠 관리
          </button>
        </nav>
      </div>

      {/* 통계 카드 */}
      {activeTab !== 'site-contents' && activeTab !== 'subscription-billing' && (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {activeTab === 'companies' ? (
          <>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Building2 className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        총 임대관리사업자
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {rmcList?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        활성화된 고객사
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {rmcList?.filter(rmc => rmc.activation_status === 'active').length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        계정 생성 대기
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {rmcList?.filter(rmc => rmc.activation_status === 'pending_activation' || rmc.activation_status === 'account_created').length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        총 등록 고객사
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {rmcList?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : activeTab === 'applications' ? (
          <>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserPlus className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        총 가입 신청
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {applicationsList?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        승인 대기
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {applicationsList?.filter(app => app.status === 'pending_approval').length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        승인 완료
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {applicationsList?.filter(app => app.status === 'approved').length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <X className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        거부됨
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {applicationsList?.filter(app => app.status === 'rejected').length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : activeTab === 'promotions' ? (
          <>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Building2 className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        총 프로모션
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {promotionsList?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        활성 프로모션
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {promotionsList?.filter(promo => promo.is_active).length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        비활성 프로모션
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {promotionsList?.filter(promo => !promo.is_active).length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserPlus className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        총 신청 수
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {promotionsList?.reduce((sum, promo) => sum + (promo.application_count || 0), 0) || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
      )}

      {/* 사이트 콘텐츠 관리 탭 - 전체 레이아웃 */}
      {activeTab === 'site-contents' ? (
        <div className="-mx-4 sm:-mx-6 lg:-mx-8">
          <div className="px-4 sm:px-6 lg:px-8">
            <SiteContentManagement />
          </div>
        </div>
      ) : activeTab === 'subscription-billing' ? (
        <SubscriptionBillingManagement companies={rmcList || []} />
      ) : (
        <>
      {/* 검색 및 필터 */}
      {activeTab !== 'site-contents' && activeTab !== 'subscription-billing' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder={
                      activeTab === 'companies' ? "임대관리사업자 검색..." :
                        activeTab === 'applications' ? "가입 신청 검색..." :
                          "프로모션 검색..."
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
              <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                <Filter className="mr-2 h-4 w-4" />
                필터
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 목록 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {activeTab === 'companies' ? (
          <ul className="divide-y divide-gray-200">
            {isLoading ? (
              <li className="px-4 py-4">
                <div className="animate-pulse flex space-x-4">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </li>
            ) : filteredRMCList.length === 0 ? (
              <li className="px-4 py-8 text-center">
                <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">임대관리사업자가 없습니다</h3>
                <p className="mt-1 text-sm text-gray-500">
                  첫 번째 임대관리사업자를 등록해보세요.
                </p>
              </li>
            ) : (
              filteredRMCList.map((rmc) => (
                <li key={rmc.id}>
                  <div className="px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">{rmc.name}</p>
                          {getActivationStatusBadge(rmc.activation_status)}
                        </div>
                        <p className="text-sm text-gray-500">{rmc.email}</p>
                        <p className="text-sm text-gray-500">{rmc.phone}</p>
                        <p className="text-sm text-gray-500">대표: {rmc.representative_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewDetail(rmc)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Eye className="mr-1 h-3 w-3" />
                        상세
                      </button>
                      {!adminStatusMap[rmc.id]?.has_admin && (
                        <button
                          onClick={() => {
                            setSelectedRMC(rmc);
                            setShowCreateAccountModal(true);
                          }}
                          className="inline-flex items-center px-3 py-1 border border-blue-300 shadow-sm text-sm font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <UserPlus className="mr-1 h-4 w-4" />
                          관리자 계정 만들기
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(rmc)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Edit className="mr-1 h-3 w-3" />
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteRMC(rmc.id)}
                        className="inline-flex items-center px-2 py-1 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        삭제
                      </button>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : activeTab === 'applications' ? (
          <ul className="divide-y divide-gray-200">
            {applicationsLoading ? (
              <li className="px-4 py-4">
                <div className="animate-pulse flex space-x-4">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </li>
            ) : applicationsList?.length === 0 ? (
              <li className="px-4 py-8 text-center">
                <UserPlus className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">가입 신청이 없습니다</h3>
                <p className="mt-1 text-sm text-gray-500">
                  새로운 가입 신청이 들어오면 여기에 표시됩니다.
                </p>
              </li>
            ) : (
              applicationsList
                ?.filter(app =>
                  app.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  app.representative_name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((application) => (
                  <li key={application.id}>
                    <div className="px-4 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <UserPlus className="h-6 w-6 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{application.company_name}</p>
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Clock className="mr-1 h-3 w-3" />
                              승인 대기
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{application.email}</p>
                          <p className="text-sm text-gray-500">{application.phone}</p>
                          <p className="text-sm text-gray-500">대표: {application.representative_name}</p>
                          <p className="text-xs text-gray-400">
                            신청일: {new Date(application.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowApplicationDetailModal(true);
                          }}
                          className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <Eye className="mr-1 h-3 w-3" />
                          상세보기
                        </button>
                      </div>
                    </div>
                  </li>
                ))
            )}
          </ul>
        ) : activeTab === 'promotions' ? (
          <ul className="divide-y divide-gray-200">
            {promotionsLoading ? (
              <li className="px-4 py-4">
                <div className="animate-pulse flex space-x-4">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </li>
            ) : !promotionsList || promotionsList.length === 0 ? (
              <li className="px-4 py-8 text-center">
                <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">프로모션이 없습니다</h3>
                <p className="mt-1 text-sm text-gray-500">
                  첫 번째 프로모션을 생성해보세요.
                </p>
                <p className="mt-1 text-xs text-red-500">
                  Debug: promotionsList = {JSON.stringify(promotionsList)}
                </p>
              </li>
            ) : (
              promotionsList
                .filter(promo =>
                  promo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  promo.description.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((promotion) => {
                  console.log('🔍 프로모션 렌더링:', promotion);
                  return (
                    <li key={promotion.id}>
                      <div className="px-4 py-4 flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <Building2 className="h-6 w-6 text-purple-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">{promotion.title}</p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${promotion.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                                }`}>
                                {promotion.is_active ? '활성' : '비활성'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">{promotion.description}</p>
                            <p className="text-sm text-gray-500">
                              신청 수: {promotion.application_count || 0}건
                            </p>
                            <p className="text-xs text-gray-400">
                              생성일: {new Date(promotion.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedPromotion(promotion);
                              setShowPromotionDetailModal(true);
                            }}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Eye className="mr-1 h-3 w-3" />
                            상세보기
                          </button>
                          <button
                            onClick={() => togglePromotionMutation.mutate(promotion.id)}
                            className={`inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded ${promotion.is_active
                              ? 'text-red-700 bg-red-50 hover:bg-red-100'
                              : 'text-green-700 bg-green-50 hover:bg-green-100'
                              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                          >
                            {promotion.is_active ? '비활성화' : '활성화'}
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('정말로 이 프로모션을 삭제하시겠습니까?')) {
                                deletePromotionMutation.mutate(promotion.id);
                              }
                            }}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            삭제
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })
            )}
          </ul>
        ) : null}
      </div>
        </>
      )}

      {/* 등록 모달 */}
      {showAddModal && (
        <AddRMCModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddRMC}
          isLoading={addRMCMutation.isLoading}
        />
      )}

      {/* 계정 생성 모달 */}
      {showCreateAccountModal && selectedRMC && (
        <CreateAccountModal
          rmc={selectedRMC}
          onClose={() => {
            setShowCreateAccountModal(false);
            setSelectedRMC(null);
          }}
          onSubmit={handleCreateAccount}
          isLoading={createAccountMutation.isLoading}
        />
      )}

      {/* 수정 모달 */}
      {showEditModal && selectedRMC && (
        <EditRMCModal
          rmc={selectedRMC}
          onClose={() => {
            setShowEditModal(false);
            setSelectedRMC(null);
          }}
          onSubmit={handleUpdateRMC}
          isLoading={updateRMCMutation.isLoading}
        />
      )}

      {/* 상세/수정 모달 */}
      {selectedRMC && !showCreateAccountModal && !showEditModal && (
        <RMCDetailModal
          rmc={selectedRMC}
          onClose={() => setSelectedRMC(null)}
          onUpdate={(data) => {
            // 수정 로직 구현
            setSelectedRMC(null);
          }}
          adminStatusMap={adminStatusMap}
          checkAdminStatus={checkAdminStatus}
          onCreateAccount={() => {
            setShowCreateAccountModal(true);
          }}
        />
      )}

      {/* 가입 신청 상세 모달 */}
      {showApplicationDetailModal && selectedApplication && (
        <ApplicationDetailModal
          application={selectedApplication}
          onClose={() => {
            setShowApplicationDetailModal(false);
            setSelectedApplication(null);
          }}
          onApprove={(initialPassword) => {
            approveApplicationMutation.mutate({
              applicationId: selectedApplication.id,
              initialPassword
            });
          }}
          onReject={(rejectionReason) => {
            rejectApplicationMutation.mutate({
              applicationId: selectedApplication.id,
              rejectionReason
            });
          }}
          isLoading={approveApplicationMutation.isLoading || rejectApplicationMutation.isLoading}
        />
      )}

      {/* 프로모션 생성/수정 모달 */}
      {showPromotionModal && (
        <PromotionModal
          promotion={selectedPromotion}
          onClose={() => {
            setShowPromotionModal(false);
            setSelectedPromotion(null);
          }}
          onSubmit={(data) => {
            if (selectedPromotion) {
              updatePromotionMutation.mutate({
                promotionId: selectedPromotion.id,
                promotionData: data
              });
            } else {
              createPromotionMutation.mutate(data);
            }
          }}
          isLoading={createPromotionMutation.isLoading || updatePromotionMutation.isLoading}
        />
      )}

      {/* 프로모션 상세 모달 */}
      {showPromotionDetailModal && selectedPromotion && (
        <PromotionDetailModal
          promotion={selectedPromotion}
          onClose={() => {
            setShowPromotionDetailModal(false);
            setSelectedPromotion(null);
          }}
          onEdit={() => {
            setShowPromotionDetailModal(false);
            setShowPromotionModal(true);
          }}
          onDelete={() => {
            if (window.confirm('정말로 이 프로모션을 삭제하시겠습니까?')) {
              deletePromotionMutation.mutate(selectedPromotion.id);
              setShowPromotionDetailModal(false);
              setSelectedPromotion(null);
            }
          }}
          onToggle={() => {
            togglePromotionMutation.mutate(selectedPromotion.id);
          }}
          isLoading={deletePromotionMutation.isLoading || togglePromotionMutation.isLoading}
        />
      )}
    </div>
  );
};

// 임대관리사업자 등록 모달
const RealtyHubAgencySelect = ({ value, onChange, currentRmcId }) => {
  const { data: agencies = [], isLoading, error } = useQuery(
    'realtyhub-agencies-for-rmc',
    () => api.get('/super-admin/realtyhub-agencies')
  );
  const selectableAgencies = agencies.filter(
    agency => !agency.rentalhub_rmc_id || agency.rentalhub_rmc_id === currentRmcId || agency.agency_id === value
  );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        RealtyHub Agency *
      </label>
      <select
        required
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading}
        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
      >
        <option value="">{isLoading ? 'Agency 조회 중...' : 'Agency를 선택하세요'}</option>
        {selectableAgencies.map(agency => (
          <option key={agency.agency_id} value={agency.agency_id}>
            {agency.name} ({agency.agency_id})
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-red-600">RealtyHub Agency 목록을 불러오지 못했습니다.</p>}
      <p className="mt-1 text-xs text-gray-500">
        SSO와 시스템 간 연동의 기준 ID입니다. 다른 RMC에 연결된 Agency는 표시되지 않습니다.
      </p>
    </div>
  );
};

const AddRMCModal = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    business_number: '',
    representative_name: '',
    subscription_plan: 'basic',
    initial_password: '',
    realtyhub_agency_id: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            임대관리사업자 등록
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <RealtyHubAgencySelect
                value={formData.realtyhub_agency_id}
                onChange={(value) => setFormData({ ...formData, realtyhub_agency_id: value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                회사명 *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                이메일 <span className="text-xs text-gray-500">(Login ID로 사용됩니다)</span> *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                초기 비밀번호 *
              </label>
              <input
                type="text"
                required
                value={formData.initial_password}
                onChange={(e) => setFormData({ ...formData, initial_password: e.target.value })}
                placeholder="6자 이상 입력하세요"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                전화번호 *
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                주소 *
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                사업자등록번호 *
              </label>
              <input
                type="text"
                required
                value={formData.business_number}
                onChange={(e) => setFormData({ ...formData, business_number: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                대표자명 *
              </label>
              <input
                type="text"
                required
                value={formData.representative_name}
                onChange={(e) => setFormData({ ...formData, representative_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                구독 플랜
              </label>
              <select
                value={formData.subscription_plan}
                onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="basic">기본</option>
                <option value="premium">프리미엄</option>
                <option value="enterprise">엔터프라이즈</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? '등록 중...' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// CreateAccountModal (단순화된 버전)
const CreateAccountModal = ({ rmc, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    admin_name: rmc.representative_name || rmc.name || '',
    admin_email: rmc.email || '',
    admin_phone: rmc.phone || '',
    admin_password: '',
    confirm_password: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.admin_password !== formData.confirm_password) {
      toast.error('비밀번호가 일치하지 않습니다.');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              📋 {rmc.name} - 관리자 계정 생성
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>계정 생성 완료 후 바로 로그인할 수 있습니다.</strong>
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                관리자 이름 *
              </label>
              <input
                type="text"
                required
                value={formData.admin_name}
                autoComplete="name"
                onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="홍길동"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                관리자 이메일 *
              </label>
              <input
                type="email"
                required
                value={formData.admin_email}
                autoComplete="email"
                onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="admin@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                관리자 연락처 *
              </label>
              <input
                type="tel"
                required
                value={formData.admin_phone}
                autoComplete="tel"
                onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="010-1234-5678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                로그인 비밀번호 *
              </label>
              <input
                type="password"
                required
                value={formData.admin_password}
                autoComplete="new-password"
                onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="8자 이상 입력"
                minLength={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                비밀번호 확인 *
              </label>
              <input
                type="password"
                required
                value={formData.confirm_password}
                autoComplete="new-password"
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="비밀번호 재입력"
                minLength={8}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? '생성 중...' : '계정 생성'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 임대관리사업자 수정 모달
const EditRMCModal = ({ rmc, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: rmc.name,
    email: rmc.email,
    phone: rmc.phone,
    address: rmc.address,
    business_number: rmc.business_number,
    representative_name: rmc.representative_name,
    subscription_plan: rmc.subscription_plan,
    realtyhub_agency_id: rmc.realtyhub_agency_id || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            임대관리사업자 수정
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <RealtyHubAgencySelect
                value={formData.realtyhub_agency_id}
                currentRmcId={rmc.id}
                onChange={(value) => setFormData({ ...formData, realtyhub_agency_id: value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                회사명 *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                이메일 *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                전화번호 *
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                주소 *
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                사업자등록번호 *
              </label>
              <input
                type="text"
                required
                value={formData.business_number}
                onChange={(e) => setFormData({ ...formData, business_number: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                대표자명 *
              </label>
              <input
                type="text"
                required
                value={formData.representative_name}
                onChange={(e) => setFormData({ ...formData, representative_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                구독 플랜
              </label>
              <select
                value={formData.subscription_plan}
                onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="basic">기본</option>
                <option value="premium">프리미엄</option>
                <option value="enterprise">엔터프라이즈</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? '수정 중...' : '수정'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const formatDateTimeText = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR');
};

const getUsageStatusLabel = (softflo) => {
  const access = softflo?.access_control || {};
  const billing = softflo?.billing || {};
  const raw = String(
    access.usage_status ||
    billing.usage_status ||
    softflo?.usage_status ||
    ''
  ).toLowerCase();

  if (raw.includes('trial')) return 'trial';
  if (raw.includes('paid')) return 'paid';
  if (raw.includes('active')) return 'paid';

  const subscriptions = softflo?.active_subscriptions || [];
  if (subscriptions.some((item) => item?.trial_flag || item?.is_trial)) return 'trial';
  if (subscriptions.length > 0) return 'paid';
  return '-';
};

const InfoRow = ({ label, value, mono = false }) => (
  <div>
    <dt className="text-xs font-semibold uppercase text-gray-500">{label}</dt>
    <dd className={`mt-1 text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>{value || '-'}</dd>
  </div>
);

const StatusPill = ({ children, tone = 'gray' }) => {
  const tones = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
};

// 임대관리사업자 상세 모달 (SoftFlo 원장 중심)
const RMCDetailModal = ({ rmc, onClose, adminStatusMap, checkAdminStatus, onCreateAccount }) => {
  const [detail, setDetail] = useState(rmc);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const company = detail || rmc;
  const softflo = ledgerData || company?.softflo_billing || {};
  const adminInfo = adminUser || adminStatusMap?.[rmc.id]?.admin_user;
  const hasAdmin = Boolean(adminInfo || adminStatusMap?.[rmc.id]?.has_admin);
  const usageStatus = getUsageStatusLabel(softflo);
  const activeSubscriptions = softflo?.active_subscriptions || [];
  const subscriptions = softflo?.subscriptions || [];
  const billing = softflo?.billing || {};
  const accessControl = softflo?.access_control || {};

  const fetchDetail = async () => {
    if (!rmc?.id) return;
    setLoading(true);
    try {
      const data = await api.get(`/super-admin/rental-management-companies/${rmc.id}`);
      setDetail(data);
      setAdminUser(data?.admin_user || null);
      setLedgerData(data?.softflo_billing || null);
    } catch (error) {
      toast.error(error.response?.data?.message || '임대관리사업자 상세정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async ({ sync = false } = {}) => {
    if (!rmc?.id) return;
    setLedgerLoading(true);
    try {
      const data = sync
        ? await api.post(`/subscription-control/softflo-sync/${rmc.id}`)
        : await api.get(`/subscription-control/softflo-billing-status/${rmc.id}`);
      const status = data?.status || data;
      setLedgerData(status);
      setShowLedgerModal(true);
      if (sync) {
        setDetail((prev) => ({ ...(prev || company), softflo_billing: status }));
        toast.success('SoftFlo 원장을 동기화했습니다.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'SoftFlo 원장을 조회하지 못했습니다.');
    } finally {
      setLedgerLoading(false);
    }
  };

  React.useEffect(() => {
    fetchDetail();
    if (checkAdminStatus && rmc?.id) {
      checkAdminStatus(rmc.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rmc?.id]);

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative top-8 mx-auto w-full max-w-5xl rounded-md border bg-white p-5 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{company?.name} - 상세정보</h3>
            <p className="mt-1 text-sm text-gray-500">SoftFlo 원장 기준 정보와 RentalHub 관리 항목을 분리해서 표시합니다.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="닫기">
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="rounded-md border border-gray-200 p-6 text-sm text-gray-500">상세정보를 불러오는 중입니다.</div>
        ) : (
          <div className="space-y-5">
            <section className="rounded-md border border-blue-100 bg-blue-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-blue-100 pb-3">
                <div>
                  <h4 className="text-base font-semibold text-gray-900">SoftFlo 동기화 항목</h4>
                  <p className="mt-1 text-sm text-gray-600">조직, 구독, 결제 상태, 크레딧은 SoftFlo 원장을 기준으로 봅니다.</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => loadLedger({ sync: false })}
                    disabled={ledgerLoading}
                    className="rounded-md border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    원장 조회
                  </button>
                  <button
                    type="button"
                    onClick={() => loadLedger({ sync: true })}
                    disabled={ledgerLoading}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    동기화 후 조회
                  </button>
                </div>
              </div>

              <dl className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <InfoRow label="SoftFlo 조직 ID" value={softflo?.softflo_organization_id} mono />
                <InfoRow label="SoftFlo 영업대행 코드" value={softflo?.softflo_agency_code} mono />
                <InfoRow label="조직 상태" value={softflo?.organization_status} />
                <InfoRow label="연결 상태" value={softflo?.provisioning_status} />
                <InfoRow label="구독 상태" value={<StatusPill tone={usageStatus === 'paid' ? 'green' : usageStatus === 'trial' ? 'blue' : 'gray'}>{usageStatus}</StatusPill>} />
                <InfoRow label="크레딧 잔액" value={`${Number(softflo?.credit_balance || 0).toLocaleString()} credits`} />
                <InfoRow label="미납 여부" value={billing?.is_paid === false ? <StatusPill tone="red">미납</StatusPill> : <StatusPill tone="green">정상</StatusPill>} />
                <InfoRow label="등록 제한" value={accessControl?.restrict_new_records ? <StatusPill tone="red">매물/광고 등록 제한</StatusPill> : <StatusPill tone="green">허용</StatusPill>} />
                <InfoRow label="마지막 동기화" value={formatDateTimeText(softflo?.last_synced_at)} />
              </dl>

              <div className="mt-4 overflow-hidden rounded-md border border-blue-100 bg-white">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">플랜</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">코드</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">주기</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">상태</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(activeSubscriptions.length ? activeSubscriptions : subscriptions).length > 0 ? (
                      (activeSubscriptions.length ? activeSubscriptions : subscriptions).map((item, index) => (
                        <tr key={`${item?.code || item?.plan_code || index}`}>
                          <td className="px-3 py-2 text-gray-900">{item?.plan_name || item?.name || '-'}</td>
                          <td className="px-3 py-2 font-mono text-gray-700">{item?.code || item?.plan_code || '-'}</td>
                          <td className="px-3 py-2 text-gray-700">{item?.billing_cycle || item?.cycle || '-'}</td>
                          <td className="px-3 py-2 text-gray-700">{item?.status || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-3 py-4 text-center text-gray-500">활성 구독 정보가 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-md border border-gray-200 p-4">
              <h4 className="border-b pb-3 text-base font-semibold text-gray-900">RentalHub 관리 항목</h4>
              <dl className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <InfoRow label="RMC ID" value={company?.id} mono />
                <InfoRow label="회사명" value={company?.name} />
                <InfoRow label="대표자명" value={company?.representative_name} />
                <InfoRow label="사업자등록번호" value={company?.business_number} mono />
                <InfoRow label="연락처" value={company?.phone} />
                <InfoRow label="이메일" value={company?.email} />
                <InfoRow label="주소" value={company?.address} />
                <InfoRow label="RealtyHub Agency ID" value={company?.realtyhub_agency_id || '미연결'} mono />
                <InfoRow label="RentalHub 활성 상태" value={company?.activation_status === 'active' ? <StatusPill tone="green">활성</StatusPill> : <StatusPill tone="yellow">비활성</StatusPill>} />
              </dl>
            </section>

            <section className="rounded-md border border-gray-200 p-4">
              <div className="flex items-center justify-between border-b pb-3">
                <h4 className="text-base font-semibold text-gray-900">관리자 계정 정보</h4>
                {!hasAdmin && onCreateAccount && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onCreateAccount();
                    }}
                    className="inline-flex items-center rounded-md border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    관리자 계정 만들기
                  </button>
                )}
              </div>
              {hasAdmin ? (
                <dl className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <InfoRow label="관리자 이름" value={adminInfo?.name} />
                  <InfoRow label="로그인 이메일" value={adminInfo?.email} mono />
                  <InfoRow label="연락처" value={adminInfo?.phone} />
                  <InfoRow label="계정 상태" value={adminInfo?.is_active ? <StatusPill tone="green">활성</StatusPill> : <StatusPill tone="red">비활성</StatusPill>} />
                  <InfoRow label="승인 상태" value={adminInfo?.is_approved ? <StatusPill tone="blue">승인</StatusPill> : <StatusPill tone="yellow">승인 대기</StatusPill>} />
                </dl>
              ) : (
                <div className="mt-4 rounded-md bg-gray-50 p-4 text-sm text-gray-500">아직 관리자 계정이 생성되지 않았습니다.</div>
              )}
            </section>
          </div>
        )}

        <div className="mt-6 flex justify-end border-t pt-4">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            닫기
          </button>
        </div>
      </div>

      {showLedgerModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 className="text-lg font-semibold text-gray-900">SoftFlo 원장 조회</h4>
              <button onClick={() => setShowLedgerModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="닫기">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-5 p-5">
              <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InfoRow label="SoftFlo 조직 ID" value={softflo?.softflo_organization_id} mono />
                <InfoRow label="조직 상태" value={softflo?.organization_status} />
                <InfoRow label="이용 상태" value={usageStatus} />
                <InfoRow label="크레딧 잔액" value={`${Number(softflo?.credit_balance || 0).toLocaleString()} credits`} />
                <InfoRow label="미납 여부" value={billing?.is_paid === false ? '미납' : '정상'} />
                <InfoRow label="등록 제한" value={accessControl?.restrict_new_records ? '매물/광고 등록 제한' : '허용'} />
              </dl>

              <div className="rounded-md border border-gray-200">
                <div className="border-b bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">활성 구독</div>
                <div className="divide-y divide-gray-100">
                  {activeSubscriptions.length > 0 ? activeSubscriptions.map((item, index) => (
                    <div key={`${item?.code || item?.plan_code || index}`} className="grid grid-cols-1 gap-2 px-3 py-3 text-sm md:grid-cols-4">
                      <span className="font-medium text-gray-900">{item?.plan_name || item?.name || '-'}</span>
                      <span className="font-mono text-gray-700">{item?.code || item?.plan_code || '-'}</span>
                      <span className="text-gray-700">{item?.billing_cycle || item?.cycle || '-'}</span>
                      <span className="text-gray-700">{item?.status || '-'}</span>
                    </div>
                  )) : (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">활성 구독이 없습니다.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <button
                type="button"
                onClick={() => loadLedger({ sync: true })}
                disabled={ledgerLoading}
                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                다시 동기화
              </button>
              <button onClick={() => setShowLedgerModal(false)} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 임대관리사업자 상세 모달 (legacy)
// eslint-disable-next-line no-unused-vars
const LegacyRMCDetailModal = ({ rmc, onClose, onUpdate, adminStatusMap, checkAdminStatus, onCreateAccount }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showPasswordInfo, setShowPasswordInfo] = useState(false);
  const [passwordInfo, setPasswordInfo] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 관리자 계정 정보 조회
  const fetchAdminUser = async () => {
    if (!rmc || !rmc.id) {
      console.log('RMC 정보가 없습니다.');
      setAdminUser(null);
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`/super-admin/rental-management-companies/${rmc.id}`);
      console.log('관리자 정보 조회 응답 전체:', response);
      
      // api.get()은 response.data를 반환하므로 response 자체가 데이터
      const data = response;
      console.log('파싱된 data:', data);
      console.log('data 타입:', typeof data);
      console.log('data 키들:', data ? Object.keys(data) : 'data is null/undefined');
      
      if (data && typeof data === 'object' && 'admin_user' in data) {
        console.log('관리자 계정 정보:', data.admin_user);
        setAdminUser(data.admin_user);
      } else {
        console.log('관리자 계정이 없습니다. data:', data);
        setAdminUser(null);
      }
    } catch (error) {
      console.error('관리자 정보 조회 실패:', error);
      console.error('에러 상세:', error.response?.data || error.message);
      console.error('에러 스택:', error.stack);
      setAdminUser(null);
    } finally {
      setLoading(false);
    }
  };

  // 패스워드 정보 조회
  const getPasswordInfoMutation = useMutation(
    (userId) => api.get(`/auth/users/${userId}/password`),
    {
      onSuccess: (data) => {
        setPasswordInfo(data);
        setShowPasswordInfo(true);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '패스워드 정보 조회에 실패했습니다.');
      }
    }
  );

  // 패스워드 설정
  const setPasswordMutation = useMutation(
    ({ userId, password }) => api.post(`/auth/users/${userId}/set-password`, { password }),
    {
      onSuccess: (data) => {
        setShowPasswordInfo(false);
        setPasswordInfo(null);
        toast.success('패스워드가 설정되었습니다!');
        alert(`✅ 패스워드 설정 완료!\n\n📧 이메일: ${data.email}\n🔑 새 패스워드: ${data.new_password}\n\n💡 이 정보를 안전한 곳에 보관하세요.`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '패스워드 설정에 실패했습니다.');
      }
    }
  );

  // 비밀번호 재설정
  const resetPasswordMutation = useMutation(
    ({ rmcId, newPassword }) => api.post(`/super-admin/rental-management-companies/${rmcId}/reset-password`, { new_password: newPassword }),
    {
      onSuccess: (data) => {
        setShowResetPassword(false);
        setNewPassword('');
        setConfirmPassword('');
        toast.success('비밀번호가 재설정되었습니다!');

        const loginInfo = data.login_info;
        alert(`✅ 비밀번호 재설정 완료!\n\n📧 로그인 이메일: ${loginInfo.email}\n🔑 새 비밀번호: ${loginInfo.password}\n\n💡 이 정보를 안전한 곳에 보관하세요.`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '비밀번호 재설정에 실패했습니다.');
      }
    }
  );

  // 초기 비밀번호로 리셋
  const resetToDefaultPasswordMutation = useMutation(
    (rmcId) => api.post(`/super-admin/rental-management-companies/${rmcId}/reset-password-to-default`, {}),
    {
      onSuccess: (data) => {
        toast.success('비밀번호가 초기 비밀번호로 재설정되었습니다!');
        const loginInfo = data.login_info;
        alert(`✅ 비밀번호 초기화 완료!\n\n📧 로그인 이메일: ${loginInfo.email}\n🔑 초기 비밀번호: ${loginInfo.password}\n\n💡 이 정보를 안전한 곳에 보관하세요.`);
        fetchAdminUser(); // 관리자 정보 다시 조회
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '비밀번호 초기화에 실패했습니다.');
      }
    }
  );

  const handleResetPassword = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    resetPasswordMutation.mutate({
      rmcId: rmc.id,
      newPassword: newPassword
    });
  };

  const handleResetToDefaultPassword = (rmcId) => {
    if (window.confirm('정말로 이 임대관리사업자의 비밀번호를 초기 비밀번호(admin123)로 리셋하시겠습니까?')) {
      resetToDefaultPasswordMutation.mutate(rmcId);
    }
  };

  React.useEffect(() => {
    if (!rmc?.id) return;
    
    console.log('RMCDetailModal useEffect 실행:', rmc.id);
    fetchAdminUser();
    // 모달이 열릴 때 관리자 상태 확인
    if (checkAdminStatus) {
      checkAdminStatus(rmc.id);
    }
  }, [rmc?.id]); // checkAdminStatus를 의존성에서 제거하여 무한 루프 방지

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              📋 {rmc.name} - 상세정보
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* 회사 정보 */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3 border-b pb-2">
                🏢 회사 정보
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">RealtyHub Agency ID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{rmc.realtyhub_agency_id || '미연결'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">회사명</label>
                  <p className="mt-1 text-sm text-gray-900">{rmc.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">이메일</label>
                  <p className="mt-1 text-sm text-gray-900">{rmc.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">전화번호</label>
                  <p className="mt-1 text-sm text-gray-900">{rmc.phone}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">주소</label>
                  <p className="mt-1 text-sm text-gray-900">{rmc.address}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">사업자등록번호</label>
                  <p className="mt-1 text-sm text-gray-900">{rmc.business_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">대표자명</label>
                  <p className="mt-1 text-sm text-gray-900">{rmc.representative_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">구독 플랜</label>
                  <p className="mt-1 text-sm text-gray-900">{rmc.subscription_plan}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">활성화 상태</label>
                  <div className="mt-1">
                    {rmc.activation_status === 'active' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        사용 가능
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Clock className="mr-1 h-3 w-3" />
                        계정 생성 필요
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 관리자 계정 정보 */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3 border-b pb-2">
                👤 관리자 계정 정보
              </h4>
              {loading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              ) : (() => {
                // 관리자 계정이 있는지 확인
                const hasAdmin = adminUser || adminStatusMap[rmc.id]?.has_admin;
                const adminInfo = adminUser || adminStatusMap[rmc.id]?.admin_user;
                
                console.log('관리자 계정 표시 조건 확인:', {
                  adminUser,
                  adminStatusMap: adminStatusMap[rmc.id],
                  hasAdmin,
                  adminInfo,
                  rmcId: rmc.id
                });
                return hasAdmin;
              })() ? (
                <div className="space-y-3">
                  {(() => {
                    const adminInfo = adminUser || adminStatusMap[rmc.id]?.admin_user;
                    return (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">관리자 이름</label>
                          <p className="mt-1 text-sm text-gray-900 font-semibold">
                            {adminInfo?.name || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">로그인 이메일</label>
                          <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded border">
                            {adminInfo?.email || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">연락처</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {adminInfo?.phone || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">계정 상태</label>
                          <div className="mt-1">
                            {adminInfo?.is_active ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✅ 활성
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                ❌ 비활성
                              </span>
                            )}
                            {adminInfo?.is_approved !== undefined && (
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                adminInfo.is_approved 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {adminInfo.is_approved ? '✓ 승인됨' : '⏳ 승인 대기'}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                    <p className="text-sm text-blue-700">
                      <strong>💡 로그인 안내:</strong><br />
                      • 로그인 ID: {(() => {
                        const adminInfo = adminUser || adminStatusMap[rmc.id]?.admin_user;
                        return adminInfo?.email || 'N/A';
                      })()}<br />
                      • 비밀번호: 계정 생성 시 설정한 비밀번호<br />
                      • 비밀번호 분실 시 아래 버튼으로 재설정
                    </p>
                  </div>

                  {/* 패스워드 확인 버튼 */}
                  <button
                    onClick={() => {
                      const adminInfo = adminUser || adminStatusMap[rmc.id]?.admin_user;
                      const userId = adminInfo?.id;
                      if (userId) {
                        getPasswordInfoMutation.mutate(userId);
                      } else {
                        toast.error('관리자 계정 ID를 찾을 수 없습니다.');
                      }
                    }}
                    className="w-full mt-3 px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    🔍 패스워드 확인
                  </button>

                  {/* 비밀번호 재설정 버튼 */}
                  <button
                    onClick={() => setShowResetPassword(true)}
                    className="w-full mt-2 px-3 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    🔑 비밀번호 재설정
                  </button>

                  {/* 초기 비밀번호로 리셋 버튼 */}
                  <button
                    onClick={() => handleResetToDefaultPassword(rmc.id)}
                    className="w-full mt-2 px-3 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    🔄 초기 비밀번호로 리셋 (admin123)
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <UserPlus className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    아직 관리자 계정이 생성되지 않았습니다.
                  </p>
                  <p className="text-xs text-gray-400 mt-1 mb-4">
                    아래 버튼을 클릭하여 관리자 계정을 생성하세요.
                  </p>
                  {onCreateAccount && (
                    <button
                      onClick={() => {
                        onClose();
                        onCreateAccount();
                      }}
                      className="inline-flex items-center px-4 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      관리자 계정 만들기
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 비밀번호 재설정 모달 */}
          {showResetPassword && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg w-96">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  🔑 비밀번호 재설정
                </h4>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      새 비밀번호 *
                    </label>
                    <input
                      type="password"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="8자 이상 입력"
                      minLength={8}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      비밀번호 확인 *
                    </label>
                    <input
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="비밀번호 재입력"
                      minLength={8}
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResetPassword(false);
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={resetPasswordMutation.isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50"
                    >
                      {resetPasswordMutation.isLoading ? '재설정 중...' : '재설정'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* 패스워드 정보 모달 */}
          {showPasswordInfo && passwordInfo && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg w-96">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  🔍 패스워드 정보
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      이메일
                    </label>
                    <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                      {passwordInfo.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      사용자 타입
                    </label>
                    <p className="text-sm text-gray-900">
                      {passwordInfo.user_type} ({passwordInfo.role})
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      패스워드 해시
                    </label>
                    <p className="text-xs text-gray-600 font-mono bg-gray-50 p-2 rounded break-all">
                      {passwordInfo.password_hash}
                    </p>
                  </div>

                  {/* 초기 패스워드 설정 */}
                  <div className="border-t pt-4">
                    <h5 className="text-md font-medium text-gray-900 mb-3">
                      🔧 초기 패스워드 설정
                    </h5>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="새 패스워드 입력"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => setNewPassword(e.target.value)}
                        value={newPassword}
                      />
                      <button
                        onClick={() => {
                          if (newPassword.trim()) {
                            setPasswordMutation.mutate({
                              userId: passwordInfo.user_id,
                              password: newPassword
                            });
                          } else {
                            toast.error('패스워드를 입력해주세요.');
                          }
                        }}
                        disabled={setPasswordMutation.isLoading}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {setPasswordMutation.isLoading ? '설정 중...' : '패스워드 설정'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-4 border-t mt-4">
                  <button
                    onClick={() => {
                      setShowPasswordInfo(false);
                      setPasswordInfo(null);
                      setNewPassword('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 가입 신청 상세 모달
const ApplicationDetailModal = ({ application, onClose, onApprove, onReject, isLoading }) => {
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [initialPassword, setInitialPassword] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const handleApprove = (e) => {
    e.preventDefault();
    if (initialPassword.length < 6) {
      alert('초기 비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    onApprove(initialPassword);
  };

  const handleReject = (e) => {
    e.preventDefault();
    onReject(rejectionReason);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              가입 신청 상세 정보
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* 회사 정보 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">회사 정보</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">회사명</label>
                  <p className="mt-1 text-sm text-gray-900">{application.company_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">사업자등록번호</label>
                  <p className="mt-1 text-sm text-gray-900">{application.business_number}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">주소</label>
                  <p className="mt-1 text-sm text-gray-900">{application.address}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">웹사이트</label>
                  <p className="mt-1 text-sm text-gray-900">{application.website || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">예상 관리 세대 수</label>
                  <p className="mt-1 text-sm text-gray-900">{application.expected_units || 0}세대</p>
                </div>
              </div>
            </div>

            {/* 대표자 정보 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">대표자 정보</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">대표자명</label>
                  <p className="mt-1 text-sm text-gray-900">{application.representative_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">이메일</label>
                  <p className="mt-1 text-sm text-gray-900">{application.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">연락처</label>
                  <p className="mt-1 text-sm text-gray-900">{application.phone}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">담당자명</label>
                  <p className="mt-1 text-sm text-gray-900">{application.contact_person || '-'}</p>
                </div>
              </div>
            </div>

            {/* 추가 정보 */}
            {application.company_description && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">회사 소개</h4>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                  {application.company_description}
                </p>
              </div>
            )}

            {/* 신청 정보 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">신청 정보</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">신청일</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(application.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">상태</label>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Clock className="mr-1 h-3 w-3" />
                    승인 대기
                  </span>
                </div>
              </div>
            </div>

            {/* 승인/거부 폼 */}
            {!showApproveForm && !showRejectForm && (
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowRejectForm(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  거부
                </button>
                <button
                  onClick={() => setShowApproveForm(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  승인
                </button>
              </div>
            )}

            {/* 승인 폼 */}
            {showApproveForm && (
              <div className="pt-4 border-t">
                <h4 className="text-md font-semibold text-gray-900 mb-3">승인 처리</h4>
                <form onSubmit={handleApprove} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      초기 비밀번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={initialPassword}
                      onChange={(e) => setInitialPassword(e.target.value)}
                      placeholder="6자 이상 입력하세요"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      minLength={6}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      이 비밀번호를 신청자에게 전달해주세요.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowApproveForm(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {isLoading ? '승인 중...' : '승인하기'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* 거부 폼 */}
            {showRejectForm && (
              <div className="pt-4 border-t">
                <h4 className="text-md font-semibold text-gray-900 mb-3">거부 처리</h4>
                <form onSubmit={handleReject} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      거부 사유
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="거부 사유를 입력하세요 (선택사항)"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowRejectForm(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      {isLoading ? '거부 중...' : '거부하기'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 프로모션 생성/수정 모달
const PromotionModal = ({ promotion, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    title: promotion?.title || '',
    description: promotion?.description || '',
    content: promotion?.content || '',
    is_active: promotion?.is_active ?? true,
    start_date: promotion?.start_date ? new Date(promotion.start_date).toISOString().slice(0, 16) : '',
    end_date: promotion?.end_date ? new Date(promotion.end_date).toISOString().slice(0, 16) : '',
    priority: promotion?.priority || 0,
    target_audience: promotion?.target_audience || 'all',
    min_units: promotion?.min_units || '',
    max_units: promotion?.max_units || '',
    discount_type: promotion?.discount_type || '',
    discount_value: promotion?.discount_value || '',
    free_trial_days: promotion?.free_trial_days || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // 데이터 정리
    const submitData = {
      ...formData,
      min_units: formData.min_units ? parseInt(formData.min_units) : null,
      max_units: formData.max_units ? parseInt(formData.max_units) : null,
      discount_value: formData.discount_value ? parseInt(formData.discount_value) : null,
      free_trial_days: formData.free_trial_days ? parseInt(formData.free_trial_days) : null,
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
    };

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {promotion ? '프로모션 수정' : '프로모션 생성'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 기본 정보 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">기본 정보</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    프로모션 제목 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="예: 신규 고객 30일 무료 체험"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    프로모션 설명
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="프로모션에 대한 간단한 설명을 입력하세요"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    상세 내용 (HTML)
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="프로모션의 상세 내용을 HTML로 입력하세요"
                  />
                </div>
              </div>
            </div>

            {/* 프로모션 설정 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">프로모션 설정</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시작일
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    종료일
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    우선순위
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    타겟 고객
                  </label>
                  <select
                    value={formData.target_audience}
                    onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">전체</option>
                    <option value="new">신규 고객</option>
                    <option value="existing">기존 고객</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 혜택 설정 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">혜택 설정</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    할인 유형
                  </label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">선택안함</option>
                    <option value="percentage">퍼센트 할인</option>
                    <option value="fixed">고정 금액 할인</option>
                    <option value="free_trial">무료 체험</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    할인 값
                  </label>
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="할인 값"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    무료 체험 일수
                  </label>
                  <input
                    type="number"
                    value={formData.free_trial_days}
                    onChange={(e) => setFormData({ ...formData, free_trial_days: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="일수"
                  />
                </div>
              </div>
            </div>

            {/* 타겟팅 설정 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">타겟팅 설정</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    최소 관리 세대 수
                  </label>
                  <input
                    type="number"
                    value={formData.min_units}
                    onChange={(e) => setFormData({ ...formData, min_units: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="최소 세대 수"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    최대 관리 세대 수
                  </label>
                  <input
                    type="number"
                    value={formData.max_units}
                    onChange={(e) => setFormData({ ...formData, max_units: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="최대 세대 수"
                  />
                </div>
              </div>
            </div>

            {/* 활성화 설정 */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  프로모션 활성화
                </label>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? '저장 중...' : (promotion ? '수정' : '생성')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 프로모션 상세 모달
const PromotionDetailModal = ({ promotion, onClose, onEdit, onDelete, onToggle, isLoading }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              프로모션 상세 정보
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* 기본 정보 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">기본 정보</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">제목</label>
                  <p className="mt-1 text-sm text-gray-900">{promotion.title}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">설명</label>
                  <p className="mt-1 text-sm text-gray-900">{promotion.description || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">상태</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${promotion.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}>
                    {promotion.is_active ? '활성' : '비활성'}
                  </span>
                </div>
              </div>
            </div>

            {/* 프로모션 설정 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">프로모션 설정</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">시작일</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {promotion.start_date ? new Date(promotion.start_date).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">종료일</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {promotion.end_date ? new Date(promotion.end_date).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">우선순위</label>
                  <p className="mt-1 text-sm text-gray-900">{promotion.priority}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">타겟 고객</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {promotion.target_audience === 'all' ? '전체' :
                      promotion.target_audience === 'new' ? '신규 고객' : '기존 고객'}
                  </p>
                </div>
              </div>
            </div>

            {/* 혜택 정보 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">혜택 정보</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">할인 유형</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {promotion.discount_type === 'percentage' ? '퍼센트 할인' :
                      promotion.discount_type === 'fixed' ? '고정 금액 할인' :
                        promotion.discount_type === 'free_trial' ? '무료 체험' : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">할인 값</label>
                  <p className="mt-1 text-sm text-gray-900">{promotion.discount_value || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">무료 체험 일수</label>
                  <p className="mt-1 text-sm text-gray-900">{promotion.free_trial_days || '-'}</p>
                </div>
              </div>
            </div>

            {/* 통계 정보 */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">통계 정보</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">총 신청 수</label>
                  <p className="mt-1 text-sm text-gray-900">{promotion.application_count || 0}건</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">생성일</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(promotion.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* 상세 내용 */}
            {promotion.content && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">상세 내용</h4>
                <div
                  className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md"
                  dangerouslySetInnerHTML={{ __html: promotion.content }}
                />
              </div>
            )}

            {/* 버튼 */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                닫기
              </button>
              <button
                onClick={onToggle}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium border border-transparent rounded-md ${promotion.is_active
                  ? 'text-red-700 bg-red-100 hover:bg-red-200'
                  : 'text-green-700 bg-green-100 hover:bg-green-200'
                  } disabled:opacity-50`}
              >
                {isLoading ? '처리 중...' : (promotion.is_active ? '비활성화' : '활성화')}
              </button>
              <button
                onClick={onEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
              >
                수정
              </button>
              <button
                onClick={onDelete}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
